import React from 'react';
import { brand } from '../../styles/brand';

/**
 * SmartPlAIte Brand Logo Components
 * 
 * Primary Logo: Plate icon + Wordmark (for landing, onboarding, marketing)
 * Secondary Logo: Wordmark only (for in-app headers, modals)
 */

const { colors, typography, shadows } = brand;

// ═══════════════════════════════════════════════════════════════════════════════
// PLATE ICON - The "C" shaped plate with accent dot
// ═══════════════════════════════════════════════════════════════════════════════
export const PlateIcon = ({ size = 48, color = colors.accent }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    {/* Main plate arc - "C" shape */}
    <path
      d="M38 24C38 31.732 31.732 38 24 38C16.268 38 10 31.732 10 24C10 16.268 16.268 10 24 10"
      stroke={color}
      strokeWidth="3.5"
      strokeLinecap="round"
      fill="none"
    />
    {/* Accent dot - prominent */}
    <circle 
      cx="36" 
      cy="12" 
      r="5" 
      fill={color}
      style={{ filter: `drop-shadow(0 0 6px ${color}40)` }}
    />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════════
// WORDMARK - "SmartPlAIte" with styled "AI" and prominent dot on "i"
// ═══════════════════════════════════════════════════════════════════════════════
export const SmartPlAIteWordmark = ({ 
  fontSize = 24,
  accentColor = colors.accent,
  showDotGlow = true
}) => {
  // Dot size: ~1.3× normal
  const dotSize = Math.max(Math.round(fontSize * 0.18), 4);
  
  // Lower the dot significantly - almost touching the stem like reference "Ai"
  // The x-height (top of lowercase letters) is roughly 50-55% down from top
  // Position dot just above the x-height line, very close to stem
  const dotTop = Math.round(fontSize * 0.38);
  
  return (
    <span style={{
      fontSize: `${fontSize}px`,
      fontWeight: '300',
      fontFamily: "'Outfit', -apple-system, sans-serif",
      color: colors.text,
      letterSpacing: '-0.02em',
      display: 'inline-flex',
      alignItems: 'baseline',
      whiteSpace: 'nowrap',
    }}>
      {/* "SmartPl" in white */}
      <span>SmartPl</span>
      
      {/* "A" highlighted in accent (subtle, not loud) */}
      <span style={{ 
        color: accentColor,
        fontWeight: '500',
      }}>A</span>
      
      {/* Lowercase "i" with custom dot - very close to stem */}
      <span style={{ 
        position: 'relative',
        display: 'inline-block',
        color: accentColor,
        fontWeight: '500',
        marginLeft: '-1px', // Pull closer to "A"
      }}>
        {/* Custom accent dot - lowered to sit close to stem */}
        <span style={{
          position: 'absolute',
          top: `${dotTop}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          width: `${dotSize}px`,
          height: `${dotSize}px`,
          borderRadius: '50%',
          backgroundColor: accentColor,
          boxShadow: showDotGlow ? `0 0 ${dotSize}px ${accentColor}50` : 'none',
          zIndex: 1,
        }} />
        {/* Dotless i */}
        <span>ı</span>
      </span>
      
      {/* "te" in white */}
      <span style={{ color: colors.text, fontWeight: '600' }}>te</span>
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRIMARY BRAND LOGO - Plate icon + Full wordmark + Tagline
// ═══════════════════════════════════════════════════════════════════════════════
export const PrimaryBrandLogo = ({ 
  size = 'large',  // 'small', 'medium', 'large'
  showTagline = true,
  taglineText = 'Nutritional intelligence — reshaped by AI'
}) => {
  const sizes = {
    small: { icon: 32, text: 20, tagline: 11, gap: 8 },
    medium: { icon: 40, text: 26, tagline: 12, gap: 10 },
    large: { icon: 48, text: 32, tagline: 13, gap: 12 }
  };
  
  const s = sizes[size] || sizes.medium;
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      gap: '12px',
    }}>
      {/* Plate Icon + Wordmark Row */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: `${Math.max(s.gap - 6, 4)}px`, // Tighter gap - plate closer to text
        marginBottom: '-4px',
      }}>
        <PlateIcon size={s.icon} color={colors.accent} />
        <SmartPlAIteWordmark fontSize={s.text} />
      </div>
      
      {/* Tagline */}
      {showTagline && (
        <div style={{
          fontSize: `${s.tagline}px`,
          color: colors.mutedText,
          letterSpacing: '0.03em',
          fontWeight: '400',
          fontFamily: typography.fontFamily,
        }}>
          {taglineText}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECONDARY APP LOGO - Wordmark only for in-app headers
// ═══════════════════════════════════════════════════════════════════════════════
export const AppHeaderLogo = ({ fontSize = 18 }) => (
  <SmartPlAIteWordmark fontSize={fontSize} showDotGlow={false} />
);

// ═══════════════════════════════════════════════════════════════════════════════
// COMPACT LOGO - For tight spaces
// ═══════════════════════════════════════════════════════════════════════════════
export const CompactLogo = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <PlateIcon size={28} color={colors.accent} />
    <SmartPlAIteWordmark fontSize={16} showDotGlow={false} />
  </div>
);

export default {
  PlateIcon,
  PrimaryBrandLogo,
  SmartPlAIteWordmark,
  AppHeaderLogo,
  CompactLogo
};
